/**
 * 영업팀 월간보고서 API (v2.115.0 · 2026-09-02)
 *
 * ⭐ 목록·상세·결재(승인·반려·회수)는 **지출결재와 같은 통로**(expense-api)를 그대로 쓴다.
 *   여기 있는 것은 「영업팀 월간보고서에만 있는 것」 — 중복 확인·작성·수정뿐이다.
 *
 * ⭐**숫자를 보내지 않는다.** 화면이 서버에 주는 것은 대상 연·월과 기타 특이사항뿐이고,
 *   집계는 서버가 매출 분석 미리보기와 같은 함수로 만들어 상신 시점에 굳힌다.
 *   그래서 여기 어떤 함수에도 금액 인자가 없다 — 있으면 사람이 결재 문서의 숫자를 바꿀 수 있게 된다.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

/** 그 달을 지금 올릴 수 있나 (GET /expense/sales-report/check) */
export interface SalesReportCheck {
  /** 서버가 정규화한 대상 월 'YYYY-MM' — 화면이 만든 문자열 대신 이 값을 쓴다 */
  periodYm: string;
  canSubmit: boolean;
  /**
   * 못 올리는 이유 (사람이 읽을 문구). canSubmit 이 true 면 null.
   * ⚠화면에서 다시 문구를 만들지 말고 **그대로** 보여 준다 — 서버가 이미 한국어로 쓴다.
   */
  reason: string | null;
  /** 이미 있는 그 달 문서 — 있으면 새로 만드는 대신 그 문서로 보낸다 */
  existing: { docId: number; docNo: string; status: string } | null;
}

/**
 * 상세(GET /expense/docs/:id)의 salesReport 칸 — 다른 종류면 null.
 *
 * ⚠**보고서 숫자는 여기 없다.** 상신 때 굳은 집계는 확정본 PDF 첨부 안에만 있다.
 *   화면에서 숫자를 다시 그리면 그건 「지금 값」이라 결재된 문서와 어긋난다.
 */
export interface SalesReportBody {
  /** 보고 대상 월 'YYYY-MM' — 작성일이 아니다 */
  periodYm: string;
  /** 기타 특이사항 — 이 문서에서 사람이 쓰는 유일한 칸 */
  notes: string;
  /** 보고서 표지 작성자 표기 — 서버가 세션 계정으로 채운다(화면이 못 바꾼다) */
  author: { dept: string; rank: string; name: string };
  createdAt: string | null;
  updatedAt: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  const json = (await res.json()) as ApiResponse<T>;
  // 서버가 이미 사람이 읽을 한국어 문구를 준다 — 여기서 갈아 끼우지 않는다
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

async function send<T>(path: string, method: 'POST' | 'PUT', body: unknown): Promise<T> {
  return call<T>(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * [결재 상신] 을 켜기 전 확인.
 * ⚠이건 **안내용**이다 — 실제 중복 차단은 서버(POST)와 DB 가 한다.
 *   확인과 상신 사이에 남이 먼저 올릴 수 있으니, 이 결과만 믿고 화면이 성공을 단정하면 안 된다.
 */
export async function checkSalesReport(year: number, month: number): Promise<SalesReportCheck> {
  const qs = new URLSearchParams({ year: String(year), month: String(month) });
  return call(`/expense/sales-report/check?${qs.toString()}`);
}

/** 작성 — submit=false 면 임시저장(작성중), true 면 그 자리에서 상신(검토대기) */
export async function createSalesReport(input: {
  year: number;
  month: number;
  notes: string;
  submit: boolean;
}): Promise<{ docId: number; docNo: string; status: string; periodYm: string }> {
  return send('/expense/sales-report', 'POST', input);
}

/**
 * 수정 — 작성자 본인의 작성중·반려 문서만.
 * ⭐대상 월은 못 바꾼다(한 달에 한 건). 다른 달을 올리려면 새로 만든다.
 */
export async function updateSalesReport(
  docId: number,
  input: { notes: string; submit: boolean },
): Promise<{ docId: number; docNo: string; status: string }> {
  return send(`/expense/sales-report/${docId}`, 'PUT', input);
}

/** '2026-08' → '2026년 8월'. 형식이 아니면 받은 값 그대로 */
export function salesPeriodText(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  return m ? `${m[1]}년 ${Number(m[2])}월` : ym;
}
